import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

/**
 * FilterButton - Industrial-grade reusable filter action chip.
 * Shared across Catalog, Terminal, and History.
 */
export const FilterButton = ({
    id,
    active,
    label,
    subLabel,
    icon: Icon,
    count,
    onClick,
    activeClass = 'bg-primary border-primary text-white shadow-primary/20',
    iconColor = 'text-slate-400',
    activeIconColor = 'bg-white/20 text-accent',
    isDropdownTrigger,
    isExpandedProp,
    className = ""
}) => (
    <button
        onClick={() => onClick(id)}
        className={`flex items-center justify-between w-full px-5 py-4 rounded-2xl border-2 transition-all group relative overflow-hidden active:scale-95 cursor-pointer ${active
            ? `${activeClass} shadow-xl scale-[1.02] z-10`
            : 'border-transparent bg-white hover:bg-slate-50 text-slate-500 hover:scale-[1.01] shadow-sm'
            } ${className}`}
    >
        <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${active ? activeIconColor : `bg-slate-50 ${iconColor} group-hover:scale-110`
                }`}>
                {count !== undefined ? (
                    <span className="text-[13px] font-black tabular-nums leading-none tracking-tighter">
                        {count}
                    </span>
                ) : Icon ? (
                    <Icon size={18} strokeWidth={2.5} />
                ) : (
                    <div className="w-4 h-4 rounded-full bg-slate-200/50" />
                )}
            </div>
            <div className="flex flex-col items-start text-left flex-1 min-w-0">
                <span
                    title={typeof label === 'string' || typeof label === 'number' ? label : ''}
                    className={`block text-[13px] font-black uppercase tracking-wider italic leading-tight truncate w-full ${active ? 'text-white' : 'text-primary'}`}
                >
                    {typeof label === 'string' || typeof label === 'number' ? label : 'UNNAMED'}
                </span>
                {subLabel && (
                    <span
                        title={subLabel}
                        className={`block text-[10px] font-bold uppercase tracking-widest leading-none mt-1 truncate w-full ${active ? 'text-white/60' : 'text-slate-400'}`}
                    >
                        {subLabel}
                    </span>
                )}
            </div>
        </div>

        <div className="flex items-center gap-2">
            {isDropdownTrigger && (
                <div className={`ml-1 flex items-center justify-center transition-transform duration-300 ${isExpandedProp ? 'rotate-180' : ''} ${active ? 'text-white/70' : 'text-slate-300'}`}>
                    <ChevronDown size={16} />
                </div>
            )}
        </div>

        {/* Premium Indicator Bar */}
        {active && (
            <div className="absolute left-0 top-4 bottom-4 w-1 bg-accent rounded-r-full shadow-[0_0_10px_rgba(255,200,0,0.5)]" />
        )}
    </button>
);

const EMPTY_ARRAY = [];

/**
 * FilterGroup - Accordion-style wrapper for filter buttons.
 */
export const FilterGroup = ({
    title,
    label,
    icon: Icon,
    children,
    defaultExpanded = false,
    isOpenDefault = false,
    hasActiveFilter = false,
    // Controlled props for Accordion
    isExpanded: isExpandedProp,
    onToggle,
    hideItemArrows = false
}) => {
    // Inner state for uncontrolled use
    const [innerExpanded, setInnerExpanded] = useState(defaultExpanded || isOpenDefault || hasActiveFilter);
    const isExpanded = isExpandedProp !== undefined ? isExpandedProp : innerExpanded;
    const displayLabel = title || label;

    const toggle = () => {
        if (onToggle) {
            onToggle();
        } else {
            setInnerExpanded(!innerExpanded);
        }
    };

    const childrenArray = React.Children.toArray(children);
    const activeChild = childrenArray.find(child => child.props?.active);

    const visibleChildren = isExpanded
        ? childrenArray
        : (activeChild ? [activeChild] : childrenArray);

    const enhancedChildren = visibleChildren.map(child => {
        if (React.isValidElement(child)) {
            const isActive = child.props.active;
            return React.cloneElement(child, {
                onClick: (id) => {
                    // When collapsed, clicking the active child (dropdown trigger) should expand the group
                    if (!isExpanded) {
                        toggle();
                    } else {
                        // When expanded, if we click the active item again, we fold the group (Universal Plegado)
                        if (isActive) {
                            toggle();
                        } else if (child.props.onClick) {
                            // Normal behavior for other items
                            child.props.onClick(id);
                        }
                    }
                },
                isDropdownTrigger: !hideItemArrows && isActive,
                isExpandedProp: isExpanded
            });
        }
        return child;
    });

    return (
        <div className="flex flex-col relative transition-all duration-300">
            <button
                type="button"
                className="flex items-center justify-between h-10 mb-2 w-full pr-4 cursor-pointer group outline-none p-0"
                onClick={toggle}
            >
                <div className="flex items-center gap-2">
                    <Icon size={12} className="text-amber-600 group-hover:scale-125 transition-transform" strokeWidth={3} />
                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 group-hover:text-primary transition-colors truncate max-w-[180px]">{displayLabel}</span>
                </div>
                <ChevronDown size={14} className={`text-slate-300 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} strokeWidth={3} />
            </button>
            <div className="flex flex-col gap-2">
                {enhancedChildren}
            </div>
        </div>
    );
};
